import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { cn, formatHuf } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useMemo, useState } from 'react';
import { PriceBreakdownDialog } from '../components/PriceBreakdownDialog';
import {
  RentalDateRangeCalendar,
  parseISODate,
  toISODate,
} from '../components/RentalDateRangeCalendar';
import { ReservationTimer } from '../components/ReservationTimer';
import { formatCurrency } from '../mock-data';
import type { CartPricing } from '../types';
import { CATEGORY_LABEL_KEYS, PricingTier } from '../types';
import { useWizardStore } from '../wizard-store';

/** E-105 Story 105-5: chip-preset → start/end dátum számítás (ISO `YYYY-MM-DD`). */
type DateChipId = 'tomorrow' | 'twoDays' | 'weekend' | 'oneWeek' | 'custom';

function computeChipDates(chip: DateChipId): { start: string; end: string } | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // LOCAL-time formázás (NEM toISOString → UTC) — különben UTC+ TZ-ben az előző
  // napot adja (a chip-preset start-dátuma „tegnap" lenne Budapesten).
  const toIso = (d: Date) => toISODate(d);
  const addDays = (d: Date, days: number) => {
    const result = new Date(d);
    result.setDate(result.getDate() + days);
    return result;
  };

  switch (chip) {
    case 'tomorrow':
      return { start: toIso(today), end: toIso(addDays(today, 1)) };
    case 'twoDays':
      return { start: toIso(today), end: toIso(addDays(today, 2)) };
    case 'weekend': {
      // Következő vasárnap (ha ma vasárnap → jövő vasárnap)
      const day = today.getDay(); // 0=Vas, 1=Hé, ..., 6=Szo
      const daysUntilSunday = day === 0 ? 7 : 7 - day;
      return { start: toIso(today), end: toIso(addDays(today, daysUntilSunday)) };
    }
    case 'oneWeek':
      return { start: toIso(today), end: toIso(addDays(today, 7)) };
    case 'custom':
      return null; // Egyedi → user-választás a date input-ban
  }
}

const TIER_LABEL_KEYS: Record<PricingTier, string> = {
  [PricingTier.HOURLY]: 'rental.wizard.tierHourly',
  [PricingTier.DAILY]: 'rental.wizard.tierDaily',
  [PricingTier.WEEKLY]: 'rental.wizard.tierWeekly',
  [PricingTier.MONTHLY]: 'rental.wizard.tierMonthly',
};

/** Get a default end time = start time + 1 hour. */
function getDefaultEndTime(startTime: string): string {
  const [hStr] = startTime.split(':');
  const h = Math.min((Number(hStr) || 0) + 1, 23);
  return `${String(h).padStart(2, '0')}:00`;
}

export function DetailsStep() {
  const { t } = useTranslation();
  const {
    partner,
    cart,
    startDate,
    endDate,
    startTime,
    endTime,
    useHourlyBilling,
    pricing: pricingRaw,
    setDates,
    setStartTime,
    setEndTime,
    setUseHourlyBilling,
    notes,
    setNotes,
    selectedPackage,
    packageClassifiedItems,
    consignmentQuantities,
  } = useWizardStore();

  const pricing = pricingRaw as CartPricing | null;

  // Format date for input — LOCAL-time (toISODate), NEM toISOString (UTC).
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return toISODate(date);
  };

  // Initialize dates if not set
  const [localStartDate, setLocalStartDate] = useState(() => {
    if (startDate) return formatDateForInput(startDate);
    const today = new Date();
    return formatDateForInput(today);
  });

  const [localEndDate, setLocalEndDate] = useState(() => {
    if (endDate) return formatDateForInput(endDate);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateForInput(tomorrow);
  });

  // Update store when dates change
  useEffect(() => {
    if (localStartDate && localEndDate) {
      // parseISODate = LOCAL-midnight Date (NEM `new Date("YYYY-MM-DD")` ami UTC-t
      // parse-ol) — a store-ban így local-time-konzisztens dátum kerül.
      const start = parseISODate(localStartDate);
      const end = parseISODate(localEndDate);
      if (start && end && start <= end) {
        setDates(start, end);
      }
    }
  }, [localStartDate, localEndDate, setDates]);

  // Check whether any cart item supports hourly billing
  const hasHourlyRateEquipment = useMemo(
    () => cart.some(item => item.equipment.hourlyRate != null && item.equipment.hourlyRate > 0),
    [cart]
  );

  // Check if start and end dates are the same day (time selection is relevant)
  const isSameDay = localStartDate === localEndDate;

  // CR-F7: Auto-reset hourly billing when dates change to non-same-day
  useEffect(() => {
    if (!isSameDay && useHourlyBilling) {
      setUseHourlyBilling(false);
    }
  }, [isSameDay, useHourlyBilling, setUseHourlyBilling]);

  // Initialize time fields — always default to current time if not set
  useEffect(() => {
    if (!startTime) {
      const now = new Date();
      const defaultStart = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setStartTime(defaultStart);
    }
    if (useHourlyBilling && !endTime && startTime) {
      setEndTime(getDefaultEndTime(startTime));
    }
  }, [useHourlyBilling, startTime, endTime, setStartTime, setEndTime]);

  // Discount percentage: discountTotal as % of subtotal (subtotal = full amount before discount)
  const discountPercent =
    pricing && pricing.discountTotal > 0 && pricing.subtotal > 0
      ? Math.round((pricing.discountTotal / pricing.subtotal) * 100)
      : 0;

  // Display tier from first cart item (all items share the same duration/tier)
  const displayTier = pricing?.items[0]?.tier;

  // Package mode: compute consignment value
  const consignmentItems = useMemo(
    () => packageClassifiedItems.filter(i => i.settlementType === 'CONSIGNMENT'),
    [packageClassifiedItems]
  );
  const consignmentTotal = useMemo(() => {
    return consignmentItems.reduce((sum, item) => {
      const qty = consignmentQuantities.get(item.id) ?? item.quantity;
      return sum + qty * (item.unitPrice ?? 0);
    }, 0);
  }, [consignmentItems, consignmentQuantities]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('rental.wizard.detailsTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('rental.wizard.detailsDesc')}
        </p>
      </div>

      {/* FE-C: reservation FE-timer (vizuális hint, nincs BE-lock — Am-4 D2) */}
      <ReservationTimer />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left column - Dates & Notes */}
        <div className="space-y-6">
          {/* Selected items summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('rental.wizard.selectedItems')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('table.partner')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-[#ccfbf1]">{partner?.name}</p>
                </div>
                <span className="text-xs text-gray-400 dark:text-[#99f6e4]" aria-hidden="true">
                  ✓
                </span>
              </div>
              {cart.map(item => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-3"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.equipment.name}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-[#99f6e4]">
                      {item.equipment.brand} {item.equipment.model} •{' '}
                      {t(CATEGORY_LABEL_KEYS[item.equipment.category] ?? item.equipment.category)}
                      {item.quantity > 1 && ` • ${item.quantity} db`}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-[#99f6e4]" aria-hidden="true">
                    ✓
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Date selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('rental.wizard.rentalPeriod')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* E-105 Story 105-5: chip-based date presets (Holnap / 2 nap / Hétvége / 1 hét / Egyedi).
                  A testid a proto-layout screenshot-maskhoz kell: az isActive chip-kiemelés
                  a MAI napból számolódik (computeChipDates) → napfüggő render. */}
              <div className="flex flex-wrap gap-2" data-testid="rental-date-chip-presets">
                {(['tomorrow', 'twoDays', 'weekend', 'oneWeek', 'custom'] as DateChipId[]).map(
                  chip => {
                    const preset = computeChipDates(chip);
                    const isActive =
                      preset !== null &&
                      preset.start === localStartDate &&
                      preset.end === localEndDate;
                    return (
                      <Button
                        key={chip}
                        type="button"
                        variant={isActive ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (preset) {
                            setLocalStartDate(preset.start);
                            setLocalEndDate(preset.end);
                            // Óradíj mód nem kompatibilis chip preset-tel (eltérő napok)
                            if (useHourlyBilling) {
                              setUseHourlyBilling(false);
                            }
                          }
                          // "custom" chip = no preset, user kitölti a date input-ot alatta
                        }}
                        className={cn(
                          'min-h-[44px] min-w-[44px]',
                          isActive && 'ring-2 ring-primary/30'
                        )}
                      >
                        {t(`rental.wizard.datePicker.chip.${chip}`)}
                      </Button>
                    );
                  }
                )}
              </div>

              <RentalDateRangeCalendar
                startDate={localStartDate}
                endDate={localEndDate}
                onChange={(start, end) => {
                  setLocalStartDate(start);
                  setLocalEndDate(end);
                }}
                minDate={toISODate(new Date())}
              />

              {/* Time inputs — always visible (ADR-078: rental time handling) */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  type="time"
                  label={t('rental.wizard.startTimeLabel')}
                  value={startTime ?? ''}
                  onChange={e => setStartTime(e.target.value)}
                />
                <Input
                  type="time"
                  label={t('rental.wizard.returnTimeLabel')}
                  value={endTime ?? ''}
                  onChange={e => setEndTime(e.target.value)}
                  min={startTime ?? undefined}
                />
              </div>

              {/* Hourly billing toggle — only available when equipment supports it */}
              {hasHourlyRateEquipment && isSameDay && (
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 dark:border-[#2d7d8a] p-3 transition-colors hover:bg-gray-50 dark:hover:bg-[#0f3d47]">
                  <input
                    type="checkbox"
                    checked={useHourlyBilling}
                    onChange={e => setUseHourlyBilling(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-[#2d7d8a] text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('rental.wizard.hourlyBilling')}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                      {t('rental.wizard.hourlyBillingDesc')}
                    </p>
                  </div>
                </label>
              )}

              {pricing && displayTier !== undefined && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-300">
                    {pricing.useHourlyBilling && pricing.totalHours != null
                      ? `${t('rental.wizard.durationHours', { hours: pricing.totalHours })} • ${t(TIER_LABEL_KEYS[displayTier] ?? String(displayTier))}`
                      : `${t('rental.wizard.durationDays', { days: pricing.durationDays })} • ${t(TIER_LABEL_KEYS[displayTier] ?? String(displayTier))}`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('common.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                id="rental-notes"
                aria-label={t('rental.wizard.rentalNotesAriaLabel')}
                className="w-full rounded-md border border-gray-300 dark:border-[#2d7d8a] dark:bg-[#1f5f6b] dark:text-white p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                rows={3}
                placeholder={t('rental.rentalNotesPlaceholder')}
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right column - Pricing */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>{t('rental.wizard.priceCalc')}</CardTitle>
            </CardHeader>
            <CardContent>
              {pricing ? (
                <div className="space-y-4">
                  {/* Per-item breakdown */}
                  <div className="space-y-2">
                    {pricing.items.map(item => (
                      <div key={item.cartItemId} className="space-y-0.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-[#ccfbf1]">
                            {item.equipmentName}
                          </span>
                          <span className="text-gray-900 dark:text-white">
                            {formatCurrency(item.itemSubtotal)}
                          </span>
                        </div>
                        {item.tier === PricingTier.HOURLY &&
                          item.hourlyRate != null &&
                          item.durationHours != null && (
                            <p className="text-xs text-gray-400 dark:text-[#99f6e4]">
                              {formatCurrency(item.hourlyRate)}
                              {t('rental.wizard.perHour')} ×{' '}
                              {t('rental.wizard.durationHours', { hours: item.durationHours })}
                            </p>
                          )}
                      </div>
                    ))}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.wizard.duration')}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {pricing.useHourlyBilling && pricing.totalHours != null
                          ? t('rental.wizard.durationHours', { hours: pricing.totalHours })
                          : t('rental.wizard.durationDays', { days: pricing.durationDays })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.wizard.pricingType')}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {displayTier !== undefined
                          ? t(TIER_LABEL_KEYS[displayTier] ?? String(displayTier))
                          : '-'}
                      </span>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  {/* Amounts */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.wizard.netRentalFee')}
                      </span>
                      <span className="text-gray-900 dark:text-white" data-testid="details-net">
                        {formatCurrency(pricing.subtotal)}
                      </span>
                    </div>
                    {pricing.discountTotal > 0 && (
                      <div className="flex justify-between text-sm text-green-600 dark:text-green-300">
                        <span>
                          {discountPercent > 0
                            ? t('rental.wizard.discountPercent', { percent: discountPercent })
                            : t('rental.wizard.discount')}
                        </span>
                        <span data-testid="details-discount">
                          -{formatCurrency(pricing.discountTotal)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.wizard.vat27')}
                      </span>
                      <span className="text-gray-900 dark:text-white" data-testid="details-vat">
                        {formatCurrency(pricing.vatAmount)}
                      </span>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between font-medium">
                      <span className="flex items-center text-foreground">
                        {t('rental.wizard.rentalFeeTotal')}
                        {pricing && <PriceBreakdownDialog pricing={pricing} />}
                      </span>
                      <span className="text-lg text-primary" data-testid="details-rental-total">
                        {formatCurrency(pricing.grandTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-600">{t('rental.deposit')}</span>
                      <span className="font-medium text-amber-600" data-testid="details-deposit">
                        {formatCurrency(pricing.depositTotal)}
                      </span>
                    </div>
                  </div>

                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />

                  {/* Grand total — S3-2 v3-parity: MOST FIZETENDŐ teal hero-kártya */}
                  <div className="rounded-lg bg-primary p-5 text-white shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
                        {t('rental.wizard.payNow')}
                      </span>
                      <span
                        className="text-3xl font-bold tabular-nums"
                        data-testid="details-pay-now"
                      >
                        {formatCurrency(pricing.grandTotal + pricing.depositTotal)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs opacity-80">
                      {t('rental.wizard.payNowBreakdown', {
                        fee: formatCurrency(pricing.grandTotal),
                        deposit: formatCurrency(pricing.depositTotal),
                      })}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 dark:text-[#ccfbf1]">
                  <p>{t('rental.wizard.selectDatesForCalc')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Package consignment value card (P0-1) */}
          {selectedPackage && consignmentItems.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-base">{t('rental.consignment.deliveryNote')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {consignmentItems.map(item => {
                    const qty = consignmentQuantities.get(item.id) ?? item.quantity;
                    return (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {item.notes ?? item.id} &times; {qty}
                        </span>
                        <span className="text-amber-600 dark:text-amber-400">
                          {formatHuf((item.unitPrice ?? 0) * qty)}
                        </span>
                      </div>
                    );
                  })}
                  <hr className="border-gray-200 dark:border-[#2d7d8a]" />
                  <div className="flex justify-between font-medium">
                    <span className="text-muted-foreground">
                      {t('rental.consignment.consignmentTotal')}
                    </span>
                    <span className="text-amber-600 dark:text-amber-400">
                      {formatHuf(consignmentTotal)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('rental.consignment.consignmentNote')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Package info card */}
          {selectedPackage && (
            <Card className="mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {t('rental.packageSelect.selectedPackage', { name: '' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-foreground">{selectedPackage.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedPackage.packageCode}</p>
                  {selectedPackage.description && (
                    <p className="text-xs text-muted-foreground">{selectedPackage.description}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
